import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';
import { de, enGB, fr, es, it, nl, pl, cs } from 'date-fns/locale';
import { getCountryCodeFromLanguage, isHolidayForCountry, getNextAvailableWorkingDay } from '@/utils/holidayUtils';

export type SupportedLanguage = 'de' | 'en' | 'fr' | 'es' | 'it' | 'nl' | 'pl' | 'cs' | 'sk';

export const languageNames = {
  de: 'Deutsch',
  en: 'English',
  fr: 'Français',
  es: 'Español',
  it: 'Italiano',
  nl: 'Nederlands',
  pl: 'Polski',
  cs: 'Čeština',
  sk: 'Slovenčina'
};

// Map language codes to date-fns locales
export const languageLocales = {
  de,
  en: enGB,
  fr,
  es,
  it,
  nl,
  pl,
  cs,
  sk: cs // Use Czech locale for Slovak as they are similar
};

interface LanguageContextType {
  language: SupportedLanguage;
  setLanguage: (lang: SupportedLanguage) => void;
  getLocale: () => typeof de;
  isHoliday: (date: Date) => boolean;
  getNextAvailableDay: (date: Date) => Date;
  loading: boolean;
}

const LanguageContext = createContext<LanguageContextType>({
  language: 'de',
  setLanguage: () => {},
  getLocale: () => de,
  isHoliday: () => false,
  getNextAvailableDay: (date: Date) => date,
  loading: true,
});

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider = ({ children }: LanguageProviderProps) => {
  const { user } = useAuth();
  const [language, setLanguageState] = useState<SupportedLanguage>('de');
  const [loading, setLoading] = useState(true);

  // Load user's language preference from profile
  useEffect(() => {
    const loadLanguagePreference = async () => {
      if (!user?.id) {
        // Set loading to false immediately for non-authenticated users
        // so the app can work with default language
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('preferred_language')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error && error.code !== 'PGRST116') {
          console.error('Error loading language preference:', error);
          setLoading(false);
          return;
        }

        if (data?.preferred_language) {
          setLanguageState(data.preferred_language as SupportedLanguage);
        }
        // If no preference is saved, keep default 'de'
      } catch (error) {
        console.error('Error loading language preference:', error);
      } finally {
        setLoading(false);
      }
    };

    loadLanguagePreference();
  }, [user?.id]);

  // Update language preference in database
  const setLanguage = async (lang: SupportedLanguage) => {
    if (!user?.id) return;

    setLanguageState(lang);

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ preferred_language: lang })
        .eq('user_id', user.id);

      if (error) {
        console.error('Error updating language preference:', error);
        // Revert on error
        setLanguageState(language);
      }
    } catch (error) {
      console.error('Error updating language preference:', error);
      setLanguageState(language);
    }
  };

  const getLocale = () => {
    return languageLocales[language] || de;
  };

  const isHoliday = (date: Date): boolean => {
    const countryCode = getCountryCodeFromLanguage(language);
    return isHolidayForCountry(date, countryCode);
  };

  const getNextAvailableDay = (date: Date): Date => {
    const countryCode = getCountryCodeFromLanguage(language);
    return getNextAvailableWorkingDay(date, countryCode);
  };

  const value: LanguageContextType = {
    language,
    setLanguage,
    getLocale,
    isHoliday,
    getNextAvailableDay,
    loading,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};